import { useMemo } from "react";
import { useTenantId } from "@src/features/tenants";
import { useCameraRouteParams } from "@src/hooks/useCameraRouteParams";
import type { CameraApiResponse } from "@src/services/api/cameras-api";
import { useAlarm } from "@src/services/api/hooks";
import { cameraHoldsFeature } from "@src/features/cameras/utils/camera-features";

export function useCameraDetailsData() {
  const tenantId = useTenantId();

  const {
    siteId,
    cameraKey,
    effectiveSiteId,
    camera,
    isCameraLoading,
    isCameraFetching,
    cameraError,
    refetchCamera,
    isLegacyAppCamera,
  } = useCameraRouteParams({
    requireSiteId: true,
    requireCameraKey: true,
    fetchCamera: true,
  });

  const legacyAppFallbackCamera: CameraApiResponse | undefined = useMemo(() => {
    if (!isLegacyAppCamera || camera || !tenantId) return undefined;
    const [asset, id] = (cameraKey || "").split("@");
    return {
      site: { tenant: tenantId, id: siteId },
      key: { asset: asset || "", id: id || "" },
      isOnline: true,
      alarmStatus: null,
      snapshot: null,
    };
  }, [isLegacyAppCamera, camera, cameraKey, tenantId, siteId]);

  const effectiveCamera = camera || legacyAppFallbackCamera;

  const {
    data: alarmDetail,
    isLoading: isAlarmLoading,
    isFetching: isAlarmFetching,
    error: alarmError,
    refetch: refetchAlarm,
  } = useAlarm(effectiveSiteId, cameraKey);

  const holdsGate = cameraHoldsFeature(effectiveCamera, "gate");

  return {
    tenantId,
    siteId,
    cameraKey,
    effectiveSiteId,
    camera,
    effectiveCamera,
    isCameraLoading,
    isCameraFetching,
    cameraError,
    refetchCamera,
    isLegacyAppCamera,
    alarmDetail,
    isAlarmLoading,
    isAlarmFetching,
    alarmError,
    refetchAlarm,
    holdsGate,
  };
}
